import React from 'react';
import { ArrowRight, AlertTriangle, CheckCircle, Lightbulb, ClipboardList, Workflow } from 'lucide-react';
import { Badge } from '../../ui/badge';

const ImplantacaoVsImplementacao = () => {
  return (
    <section className="py-16 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Gráfico de Sequência */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              Entenda a Sequência Correta
            </h2>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
              {/* Implantação */}
              <div className="bg-gradient-to-br from-blue-900/40 to-zinc-900 border-2 border-blue-500 rounded-2xl p-6 w-full md:w-64 text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1º</span>
                </div>
                <h3 className="text-2xl font-bold text-blue-400 mb-2">IMPLANTAÇÃO</h3>
                <p className="text-gray-400 text-sm">Planejamento e Preparação</p>
                <div className="mt-4 py-2 px-4 bg-blue-600/20 rounded-lg">
                  <span className="text-blue-300 font-medium">3 a 4 meses</span>
                </div>
              </div>

              {/* Seta */}
              <div className="flex items-center justify-center">
                <div className="hidden md:flex items-center">
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-500 to-red-500"></div>
                  <ArrowRight className="text-red-500" size={32} />
                </div>
                <div className="md:hidden flex flex-col items-center py-2">
                  <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-red-500"></div>
                  <ArrowRight className="text-red-500 rotate-90" size={32} />
                </div>
              </div>

              {/* Implementação */}
              <div className="bg-gradient-to-br from-red-900/40 to-zinc-900 border-2 border-red-500 rounded-2xl p-6 w-full md:w-64 text-center">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2º</span>
                </div>
                <h3 className="text-2xl font-bold text-red-400 mb-2">IMPLEMENTAÇÃO</h3>
                <p className="text-gray-400 text-sm">Execução e Aplicação</p>
                <div className="mt-4 py-2 px-4 bg-red-600/20 rounded-lg">
                  <span className="text-red-300 font-medium">12 a 18 meses</span>
                </div>
              </div>
            </div>

            <p className="text-center text-yellow-400 mt-6 text-sm flex items-center justify-center gap-2">
              <AlertTriangle size={16} />
              A Implementação BIM só é possível após a conclusão da Implantação
            </p>
          </div>

          {/* Comparação Detalhada */}
          <div className="mb-12">
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-white text-center mb-6">
                Qual a diferença entre <span className="text-blue-400">Implantação</span> e <span className="text-red-400">Implementação</span>?
              </h3>
              
              <p className="text-gray-300 text-center mb-8 max-w-3xl mx-auto">
                Muitas empresas confundem esses termos e acabam <strong className="text-white">contratando um serviço pensando que estão contratando outro</strong>. 
                Entender essa diferença é fundamental para o sucesso do seu projeto BIM.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Implantação */}
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                      <ClipboardList className="text-white" size={20} />
                    </div>
                    <h4 className="text-xl font-bold text-blue-400">IMPLANTAÇÃO</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    É a fase de <strong className="text-white">planejamento, diagnóstico e preparação</strong>. 
                    Aqui definimos O QUE fazer, COMO fazer e COM O QUE fazer.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Levantamento de informações da empresa</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Diagnóstico completo (~300 páginas)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Planejamento estratégico e cronograma</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Definição de softwares e investimentos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Análise financeira e ROI projetado</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-blue-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Duração média:</span>
                      <span className="text-blue-400 font-bold">3 a 4 meses</span>
                    </div>
                  </div>
                </div>

                {/* Implementação */}
                <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <Workflow className="text-white" size={20} />
                    </div>
                    <h4 className="text-xl font-bold text-red-400">IMPLEMENTAÇÃO</h4>
                  </div>
                  <p className="text-gray-300 mb-4">
                    É a fase de <strong className="text-white">execução, aquisição e aplicação prática</strong>. 
                    Aqui EXECUTAMOS tudo o que foi planejado na implantação.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Aquisição de softwares e equipamentos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Capacitação completa das equipes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Desenvolvimento de templates e bibliotecas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Projeto piloto supervisionado</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="text-red-400 mt-0.5 flex-shrink-0" size={16} />
                      <span>Acompanhamento até autossuficiência</span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-red-500/30">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Duração média:</span>
                      <span className="text-red-400 font-bold">12 a 18 meses</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Aviso importante */}
              <div className="mt-8 bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="text-yellow-500" size={24} />
                  </div>
                  <div>
                    <h4 className="text-yellow-400 font-bold mb-2">Importante: São contratações separadas!</h4>
                    <p className="text-gray-300 text-sm">
                      Normalmente, <strong className="text-white">Implantação e Implementação são contratadas separadamente</strong>. 
                      Algumas empresas contratam apenas a Implantação para ter o diagnóstico e planejamento, 
                      e depois decidem se irão contratar a Implementação ou executar internamente. 
                      Outras preferem contratar o pacote completo desde o início.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Curso Obrigatório - BIM Bootcamp */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-purple-900/30 to-zinc-900 border border-purple-500/30 rounded-2xl p-8">
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="w-20 h-20 bg-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="text-white" size={40} />
                </div>
                <div className="flex-1">
                  <Badge className="bg-purple-600/20 text-purple-400 border-purple-500/30 mb-3">
                    Etapa Obrigatória da Implantação
                  </Badge>
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Curso de Nivelamento: <span className="text-purple-400">BIM Bootcamp</span> ou <span className="text-purple-400">BIM Mindset & Fundamentos</span>
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Antes de responder ao questionário de levantamento de informações, é <strong className="text-white">obrigatório</strong> que 
                    a equipe participe de um curso de nivelamento. Isso garante que todos saibam <strong className="text-white">o que é BIM, 
                    seu potencial, suas tecnologias e seus limites</strong> — permitindo respostas mais precisas e assertivas.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-zinc-900/50 rounded-xl p-4">
                      <h4 className="text-purple-400 font-bold mb-2">BIM Bootcamp</h4>
                      <p className="text-gray-400 text-sm mb-2">Palestra informativa e dinâmica</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li>• Duração: 4 a 8 horas</li>
                        <li>• Formato: Presencial ou online</li>
                        <li>• Ideal para: Visão geral rápida</li>
                      </ul>
                    </div>
                    <div className="bg-zinc-900/50 rounded-xl p-4">
                      <h4 className="text-purple-400 font-bold mb-2">BIM Mindset & Fundamentos</h4>
                      <p className="text-gray-400 text-sm mb-2">Curso completo e aprofundado</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li>• Duração: 16 a 24 horas</li>
                        <li>• Formato: Presencial ou online</li>
                        <li>• Ideal para: Entendimento profundo</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center gap-2 text-purple-300 text-sm">
                    <CheckCircle size={16} />
                    <span>Com esse nivelamento, sua equipe saberá exatamente o que o BIM pode oferecer antes de planejar a implantação</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Etapas da Implantação - Cards */}
          <div className="py-8">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Etapas da <span className="text-blue-400">Implantação</span>
            </h2>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              {/* Card 1 - Levantamento */}
              <div className="bg-zinc-900 border border-blue-500/50 rounded-xl p-5 w-full md:w-56 text-center hover:border-blue-400 transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-white">1</span>
                </div>
                <h4 className="text-white font-bold mb-1">Levantamento</h4>
                <p className="text-gray-500 text-xs">de Informações</p>
                <div className="mt-3 text-blue-400 text-xs">2-4 semanas</div>
              </div>

              {/* Seta */}
              <div className="hidden md:block">
                <ArrowRight className="text-blue-500" size={24} />
              </div>
              <div className="md:hidden">
                <ArrowRight className="text-blue-500 rotate-90" size={24} />
              </div>

              {/* Card 2 - Diagnóstico */}
              <div className="bg-zinc-900 border border-blue-500/50 rounded-xl p-5 w-full md:w-56 text-center hover:border-blue-400 transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-white">2</span>
                </div>
                <h4 className="text-white font-bold mb-1">Diagnóstico</h4>
                <p className="text-gray-500 text-xs">Completo</p>
                <div className="mt-3 text-blue-400 text-xs">3-6 semanas</div>
              </div>

              {/* Seta */}
              <div className="hidden md:block">
                <ArrowRight className="text-blue-500" size={24} />
              </div>
              <div className="md:hidden">
                <ArrowRight className="text-blue-500 rotate-90" size={24} />
              </div>

              {/* Card 3 - Planejamento */}
              <div className="bg-zinc-900 border border-blue-500/50 rounded-xl p-5 w-full md:w-56 text-center hover:border-blue-400 transition-all">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-white">3</span>
                </div>
                <h4 className="text-white font-bold mb-1">Planejamento</h4>
                <p className="text-gray-500 text-xs">Estratégico</p>
                <div className="mt-3 text-blue-400 text-xs">4-8 semanas</div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <div className="w-24 h-0.5 bg-blue-500"></div>
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImplantacaoVsImplementacao;
